import { Box, IconButton, useTheme } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useSwipeable } from 'react-swipeable';
import { SGalleryThumbnail } from '../thumbnail';
import { useGalleryStyles } from '../style';
import SETVIImage from '../../setvi-image';

export interface GalleryProps {
  images: string[];
  defaultOpenedImage?: string;
  numberOfPreviewThumbnails?: number;
  isFullScreen?: boolean;
  onImageClick?: (imageUrl: string) => void;
}

export const Gallery = ({
  images = [],
  defaultOpenedImage,
  numberOfPreviewThumbnails = 3,
  isFullScreen,
  onImageClick
}: GalleryProps) => {
  const [startIndex, setStartIndex] = useState<number>(0);
  const [endIndex, setEndIndex] = useState<number>(numberOfPreviewThumbnails);
  const [activeImage, setActiveImage] = useState<string | null>(
    defaultOpenedImage || images?.[0] || null
  );
  const styles = useGalleryStyles();
  const { palette } = useTheme();

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setActiveImage(prev => {
        const currentIndex = images.indexOf(prev);
        if (currentIndex < images.length - 1) {
          return images[currentIndex + 1];
        }
        return prev;
      });
    },
    onSwipedRight: () => {
      setActiveImage(prev => {
        const currentIndex = images.indexOf(prev);
        if (currentIndex > 0) {
          return images[currentIndex - 1];
        }
        return prev;
      });
    },
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 10 // min move to detect swipe event
  });
  const leftArrowDisabled = startIndex === 0;
  const rightArrowDisabled = endIndex === images.length;
  const fullScreenStyle = (disabled: boolean) => ({
    color: palette.common.white,
    opacity: disabled && 0.5
  });

  const thumbnails = useMemo(
    () => images.slice(startIndex, endIndex),
    [images, startIndex, endIndex]
  );

  const hasArrows = useMemo(
    () => images.length > numberOfPreviewThumbnails,
    [images, numberOfPreviewThumbnails]
  );

  const handleNext = () => {
    if (endIndex === images.length) return;
    setEndIndex(prev => prev + 1);
    setStartIndex(prev => prev + 1);
  };

  const handleBack = () => {
    if (startIndex === 0) return;
    setEndIndex(prev => prev - 1);
    setStartIndex(prev => prev - 1);
  };

  useEffect(() => {
    if (Array.isArray(images) && images?.length) {
      setActiveImage(images?.[0]);
    }
  }, [images]);

  return (
    <Box width="100%">
      <div
        {...handlers}
        className={styles.mainImageContainer}
        onClick={() => onImageClick?.(activeImage)}
        style={{
          height: isFullScreen ? '80vh' : '35vh',
          cursor: 'grab'
        }}>
        <SETVIImage
          className={styles.mainImage}
          src={activeImage}
          alt="main image"
          style={{
            width: '100%',
            touchAction: 'pan-y',
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        />
      </div>
      <Box mt={2} display="flex" alignItems="center">
        {hasArrows && (
          <Box>
            <IconButton
              disabled={leftArrowDisabled}
              onClick={handleBack}
              size="small">
              <ChevronLeftIcon
                style={isFullScreen && fullScreenStyle(leftArrowDisabled)}
              />
            </IconButton>
          </Box>
        )}
        {images.length > 1 && (
          <Box
            maxHeight={60}
            flexGrow={1}
            display="flex"
            justifyContent="center">
            {thumbnails.map(image => (
              <SGalleryThumbnail
                key={image}
                src={image}
                onClick={() => setActiveImage(image)}
                active={activeImage === image}
              />
            ))}
          </Box>
        )}
        {hasArrows && (
          <Box>
            <IconButton
              disabled={rightArrowDisabled}
              onClick={handleNext}
              size="small">
              <ChevronRightIcon
                style={isFullScreen && fullScreenStyle(rightArrowDisabled)}
              />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};
